import React, { useRef } from 'react'
import TableComponent, { TableComponentType } from 'UI/Table'
import { IColumn } from 'typescript/interfaces/tableUi'
import { useTableComponentStyle } from 'UI/Table/style'
import {
  camelCaseWrap,
  convertDate,
  convertEmptyField,
  convertToPercentageFormat,
  convertToPriceFormat,
  convertToPriceFormatWithSign,
  formatNegativePrice,
} from 'utils/formatters'
import { ISyndicatorReportListItem, SyndicatorDealsSortingFields } from 'typescript/interfaces/syndicators'
import { Box, IconButton, SvgIcon, Tooltip, Typography } from '@material-ui/core'
import { ReactComponent as CircleArrowIcon } from 'assets/svg/CircleArrowBottomIcon.svg'
import { ReactComponent as InfoIcon } from 'assets/svg/InfoIcon.svg'
import { mathRoundingPercentage } from 'utils/rounding'

export const TableSyndicatorReport = TableComponent as TableComponentType<ISyndicatorReportListItem>

export const arrayData = Array(20)
  .fill({
    merchant: 'MERCHANT',
    deal: 4900,
    dealEndDate: '08.30.2020',
    participation: 50,
    dealAdvance: 500,
    syndicated: 281800,
    projPayback: 479130,
    toDatePayback: 100,
    paidOff: 100,
  })
  .map((c, i) => ({
    ...c,
    id: i,
    merchant: `${c.merchant} ${i}`,
  }))

interface IUseSyndicatedDealsParams {
  handleExpand: (dealId: number) => void
}

export const useSyndicatorReport = (params?: IUseSyndicatedDealsParams) => {
  const classes = useTableComponentStyle()
  const columns = useRef<Array<IColumn<ISyndicatorReportListItem>>>([
    {
      label: 'Merchant',
      field: SyndicatorDealsSortingFields.merchant,
      bodyCellClassName: classes.secondaryBodyCell,
      sorting: true,
      render: (data) => convertEmptyField(data.merchant),
    },
    {
      label: 'Deal',
      field: SyndicatorDealsSortingFields.deal,
      bodyCellClassName: classes.secondaryBodyCell,
      sorting: true,
      render: (data) => convertEmptyField(data.deal),
    },
    {
      label: 'Type',
      tooltip: 'Deal Type',
      field: 'extendType',
      bodyCellClassName: classes.secondaryBodyCell,
      render: (data) => convertEmptyField(data.extendType ?? 'Regular'),
    },
    {
      label: (
        <>
          Start
          <br />
          Date
        </>
      ),
      tooltip: 'Deal Start Date',
      field: SyndicatorDealsSortingFields.dealStartDate,
      sorting: true,
      bodyCellClassName: classes.accentCell,
      render: (data) => convertDate(data.startDate),
    },
    {
      label: (
        <>
          End
          <br />
          Date
        </>
      ),
      tooltip: 'Deal End Date',
      field: SyndicatorDealsSortingFields.dealEndDate,
      bodyCellClassName: classes.secondaryBodyCell,
      render: (data) => convertDate(data.dealEndDate),
    },
    {
      label: 'Status',
      field: 'status',
      render: (data) => convertEmptyField(camelCaseWrap(data.status)),
    },
    {
      label: (
        <>
          Ttl
          <br />
          Advance
        </>
      ),
      tooltip: 'Total Deal Advance',
      field: SyndicatorDealsSortingFields.dealAdvance,
      bodyCellClassName: classes.accentCell,
      headerCellClassName: classes.allowTextWraping,
      sorting: true,
      render: (data) => convertToPriceFormat(data.dealAdvance),
    },
    {
      label: (
        <>
          Ttl
          <br />
          Payback
        </>
      ),
      tooltip: 'Total Deal Payback',
      field: SyndicatorDealsSortingFields.projPayback,
      bodyCellClassName: classes.secondaryBodyCell,
      headerCellClassName: classes.allowTextWraping,
      render: (data) => convertToPriceFormat(data.toDatePayback),
    },
    {
      label: 'Term / F',
      tooltip: 'Term/Factor Rate',
      field: SyndicatorDealsSortingFields.term,
      bodyCellClassName: classes.accentCell,
      render: data => {
        const active = data.activeScheduledPaymentInfo
        const initial = data.initialScheduledPaymentInfo

        const tooltipTitle = active.term !== initial.term
          ? `Initial Term: ${mathRoundingPercentage(initial.term)}`
          : ''

        return (
          <Box fontSize="1rem" display="flex" alignItems="center">
            {`${mathRoundingPercentage(active.term)}/${mathRoundingPercentage(data.factor)}`}
            {tooltipTitle && (
              <Box mt={1} ml={1}>
                <Tooltip placement="top" title={tooltipTitle}>
                  <InfoIcon color="orange" fill="orange" width={16} height={16} />
                </Tooltip>
              </Box>
            )}
          </Box>
        )
      }
    },
    {
      label: (
        <>
          Synd
          <br />
          Amount
        </>
      ),
      tooltip: 'Syndicated Amount',
      field: SyndicatorDealsSortingFields.toDatePayback,
      bodyCellClassName: classes.secondaryBodyCell,
      headerCellClassName: classes.allowTextWraping,
      sorting: true,
      render: (data) => convertToPriceFormat(data.syndicated),
    },
    {
      label: (
        <>
          Synd&nbsp;Ttl
          <br />
          Payback
        </>
      ),
      tooltip: 'Syndicator Total Payback',
      field: SyndicatorDealsSortingFields.syndicated,
      bodyCellClassName: classes.accentCell,
      headerCellClassName: classes.allowTextWraping,
      render: (data) => convertToPriceFormat(data.projPayback),
    },
    {
      label: 'Synd %',
      tooltip: 'Syndication %',
      field: SyndicatorDealsSortingFields.participation,
      bodyCellClassName: classes.accentCell,
      render: (data) => convertToPercentageFormat(data.participation),
    },
    {
      label: (
        <>
          Synd&nbsp;%&nbsp;from
          <br />
          Daily&nbsp;Payment
        </>
      ),
      tooltip: 'Syndicator of Daily Deal Payment',
      field: 'Daily Amount',
      sorting: false,
      bodyCellClassName: classes.accentCell,
      render: data => {
        const active = data.activeScheduledPaymentInfo
        const initial = data.initialScheduledPaymentInfo

        let tooltipTitle = ''

        if (active?.paymentFrequency !== initial?.paymentFrequency) {
          tooltipTitle = `Initial: ${convertToPriceFormat(initial?.amount)} ${initial?.paymentFrequency}`
        } else if (active?.amount !== initial?.amount) {
          tooltipTitle = `Initial: ${convertToPriceFormat(initial?.amount)}`
        }
        const showAmount = active.paymentFrequency === 'Daily'

        return (
          <Box fontSize="1rem" display="flex" alignItems="center">
            {convertToPriceFormat(showAmount ? active.amount : 0)}
            {tooltipTitle && showAmount && (
              <Box mt={1} ml={1}>
                <Tooltip placement="top" title={tooltipTitle}>
                  <InfoIcon color="orange" fill="orange" width={16} height={16} />
                </Tooltip>
              </Box>
            )}
          </Box>
        )
      }
    },
    {
      label: (
        <>
          Synd&nbsp;%&nbsp;from
          <br />
          Weekly&nbsp;Payment
        </>
      ),
      tooltip: 'Syndicator of Weekly Deal Payment',
      field: 'Weekly Amount',
      sorting: false,
      bodyCellClassName: classes.accentCell,
      render: data => {
        const active = data.activeScheduledPaymentInfo
        const initial = data.initialScheduledPaymentInfo

        const activeDisplayPayment = active.paymentFrequency === 'Daily' 
          ? 0 
          : active.paymentFrequency === 'Weekly' 
            ? active.amount 
            : active.amount / 2
        const initialDisplayPayment = initial.paymentFrequency === 'Daily' 
          ? 0 
          : initial.paymentFrequency === 'Weekly' 
            ? initial.amount 
            : initial.amount / 2

        const tooltipLines = [];

        if (active.paymentFrequency === 'Biweekly') {
          tooltipLines.push(`Active: ${convertToPriceFormat(active.amount)} Biweekly`);
        }

        if (active.paymentFrequency !== initial.paymentFrequency) {
          tooltipLines.push(`Initial: ${convertToPriceFormat(initial?.amount)} ${initial?.paymentFrequency}`);
        } else if (activeDisplayPayment !== initialDisplayPayment) {
          tooltipLines.push(`Initial: ${convertToPriceFormat(initial?.amount)}${active.paymentFrequency === 'Biweekly' ? ' Biweekly' : ''}`);
        }

        const showAmount = active.paymentFrequency !== 'Daily'

        const tooltipTitle = tooltipLines.length > 0 ? (
          <Box>
            {tooltipLines.map((line) => (
              <Typography key={line} color="inherit">
                {line}
              </Typography>
            ))}
          </Box>
        ) : null;

        return (
          <Box fontSize="1rem" display="flex" alignItems="center">
            {convertToPriceFormat(showAmount ? activeDisplayPayment : 0)}
            {tooltipTitle && showAmount && (
              <Box mt={1} ml={1}>
                <Tooltip placement="top" title={tooltipTitle}>
                  <InfoIcon color="orange" fill="orange" width={16} height={16} />
                </Tooltip>
              </Box>
            )}
          </Box>
        )
      }
    },
    {
      label: (
        <>
          Ttl
          <br />
          Collected
        </>
      ),
      tooltip: 'Total Collected',
      field: 'totalCollected',
      sorting: false,
      bodyCellClassName: classes.accentCell,
      headerCellClassName: classes.allowTextWraping,
      render: (data) => convertToPriceFormat(data.dealCollected),
    },
    {
      label: (
        <>
          Total %
          <br />
          Collected
        </>
      ),
      tooltip: 'Total % Collected on the Deal',
      field: 'totalPercentageCollected',
      sorting: false,
      bodyCellClassName: classes.accentCell,
      headerCellClassName: classes.allowTextWraping,
      render: (data) => convertToPercentageFormat(data.totalPercentageCollected),
    },
    {
      label: (
        <>
          Synd
          <br />
          Collected
        </>
      ),
      tooltip: 'Syndicator Collected',
      field: 'collected',
      sorting: true,
      bodyCellClassName: classes.accentCell,
      headerCellClassName: classes.allowTextWraping,
      render: (data) => convertToPriceFormat(data.collected),
    },
    {
      label: (
        <>
          Rem
          <br />
          Bal&nbsp;Ttl
        </>
      ),
      tooltip: 'Remaining Balance Total',
      field: 'remainingBalanceTotal',
      sorting: false,
      bodyCellClassName: classes.accentCell,
      headerCellClassName: classes.allowTextWraping,
      render: (data) => convertToPriceFormat(data.dealRemaining),
    },
    {
      label: (
        <>
          Rem
          <br />
          Bal&nbsp;Synd
        </>
      ),
      tooltip: 'Remaining Balance Syndicator',
      field: SyndicatorDealsSortingFields.syndicatorRemaining,
      sorting: true,
      bodyCellClassName: classes.accentCell,
      headerCellClassName: classes.allowTextWraping,
      render: (data) => convertToPriceFormat(data.remainingBalance),
    },
    {
      label: 'Synd PNL',
      field: 'pnl',
      bodyCellClassName: classes.accentCell,
      render: (data) => (
        <Tooltip
          placement="bottom-end"
          title={
            <Box>
              <Box display="flex" justifyContent="space-between">
                <Typography>Funding</Typography>
                <Box ml="1rem">{convertToPriceFormatWithSign(-data.syndicated)}</Box>
              </Box>
              <Box display="flex" justifyContent="space-between">
                <Typography>Commission</Typography>
                <Box ml="1rem">{convertToPriceFormatWithSign(-data.totalCommission)}</Box>
              </Box>
              <Box display="flex" justifyContent="space-between">
                <Typography>Legal Fee</Typography>
                <Box ml="1rem">{convertToPriceFormatWithSign(-data.legalFee)}</Box>
              </Box>
              <Box display="flex" justifyContent="space-between">
                <Typography>Mgm Fee</Typography>
                <Box ml="1rem">{convertToPriceFormatWithSign(-data.mgmFee)}</Box>
              </Box>
              <Box display="flex" justifyContent="space-between">
                <Typography>Default Fee</Typography>
                <Box ml="1rem">{convertToPriceFormatWithSign(-data.defaultFee)}</Box>
              </Box>
              <Box display="flex" justifyContent="space-between">
                <Typography>Wire Fee</Typography>
                <Box ml="1rem">{convertToPriceFormatWithSign(-data.wireFee)}</Box>
              </Box>
              <Box display="flex" justifyContent="space-between">
                <Typography>Bounce Fee</Typography>
                <Box ml="1rem">{convertToPriceFormatWithSign(-data.bounceFee)}</Box>
              </Box>
              <Box display="flex" justifyContent="space-between">
                <Typography>Bank Fee</Typography>
                <Box ml="1rem">{convertToPriceFormatWithSign(data.bankFee)}</Box>
              </Box>
              <Box display="flex" justifyContent="space-between">
                <Typography>Collected</Typography>
                <Box ml="1rem">{convertToPriceFormatWithSign(data.collected)}</Box>
              </Box>
            </Box>
          }
        >
          <Box>{formatNegativePrice(data.pnl, convertToPriceFormat)}</Box>
        </Tooltip>
      ),
    },
    {
      label: 'Total PNL',
      tooltip: 'Total Deal PNL',
      field: 'totalDealPnl',
      bodyCellClassName: classes.accentCell,
      headerCellClassName: classes.allowTextWraping,
      render: (data) => (
        <Tooltip
          placement="bottom-end"
          title={
            <Box>
              <Box display="flex" justifyContent="space-between">
                <Typography>Funding</Typography>
                <Box ml="1rem">{convertToPriceFormatWithSign(-data.totalDealFunding)}</Box>
              </Box>
              <Box display="flex" justifyContent="space-between">
                <Typography>Commission</Typography>
                <Box ml="1rem">{convertToPriceFormatWithSign(-data.totalDealCommission)}</Box>
              </Box>
              <Box display="flex" justifyContent="space-between">
                <Typography>Legal Fee</Typography>
                <Box ml="1rem">{convertToPriceFormatWithSign(-data.totalDealLegalFee)}</Box>
              </Box>
              <Box display="flex" justifyContent="space-between">
                <Typography>PSF Fee</Typography>
                <Box ml="1rem">{convertToPriceFormatWithSign(data.totalDealPsfFee)}</Box>
              </Box>
              <Box display="flex" justifyContent="space-between">
                <Typography>Bank Fee</Typography>
                <Box ml="1rem">{convertToPriceFormatWithSign(data.totalDealSyndicationFee)}</Box>
              </Box>
              <Box display="flex" justifyContent="space-between">
                <Typography>Default fee</Typography>
                <Box ml="1rem">
                  {convertToPriceFormatWithSign(data.totalDealRemaining <= 0 ? data.totalDealDefaultFee : 0)}
                </Box>
              </Box>
              <Box display="flex" justifyContent="space-between">
                <Typography>Wire fee</Typography>
                <Box ml="1rem">
                  {convertToPriceFormatWithSign(data.totalDealRemaining <= 0 ? data.totalDealWireFee : 0)}
                </Box>
              </Box>
              <Box display="flex" justifyContent="space-between">
                <Typography>Bounce fee</Typography>
                <Box ml="1rem">
                  {convertToPriceFormatWithSign(data.totalDealRemaining <= 0 ? data.totalDealBounceFee : 0)}
                </Box>
              </Box>
              <Box display="flex" justifyContent="space-between">
                <Typography>Collected</Typography>
                <Box ml="1rem">
                  {convertToPriceFormatWithSign(
                    data.totalDealRemaining <= 0
                      ? data.dealCollected - (data.totalDealDefaultFee + data.totalDealWireFee + data.totalDealBounceFee)
                      : data.dealCollected,
                  )}
                </Box>
              </Box>
            </Box>
          }
        >
          <Box>{formatNegativePrice(data.totalDealPnl, convertToPriceFormat)}</Box>
        </Tooltip>
      ),
    },
    ...(params != null
      ? [
          {
            label: '',
            field: 'status',
            render: (data: ISyndicatorReportListItem) => (
              <IconButton onClick={() => params.handleExpand(data.deal)}>
                <SvgIcon
                  style={{ transform: `rotate(${data.tableOnlyRowExpanded ? '180deg' : '0'})` }}
                  fontSize="inherit"
                  viewBox="0 0 20 20"
                  component={CircleArrowIcon}
                />
              </IconButton>
            ),
          },
        ]
      : []),
  ])

  return {
    columns: columns.current,
  }
}
